import React, { useState } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import Container from '@material-ui/core/Container'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import LinearProgress from '@material-ui/core/LinearProgress'
import Box from '@material-ui/core/Box'
import FormControl from '@material-ui/core/FormControl'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'
import { gql, useQuery, useMutation } from '@apollo/client'
import { useAuth } from 'lib/auth'
import { useSnackbarSuccess, useSnackbarError } from 'lib/snackbar'
import { PageHeader } from 'components/page-header'
import Error from 'components/error'
import { usePortal } from 'lib/portal'
import useStyles from './styles'

const GET_BOOKING_LIMIT_OPTIONS = gql`
  query($id: ID!) {
    coach(id: $id) {
      bookingLimit
    }
    bookingLimitOptions {
      label
      value
    }
  }
`

const SET_BOOKING_LIMIT = gql`
  mutation SetBookingLimit($bookingLimit: Int!) {
    setBookingLimit(bookingLimit: $bookingLimit) {
      success
      message
      coach {
        bookingLimit
      }
    }
  }
`

const BookingLimitsPage = () => {
  const { path } = useRouteMatch()
  const { user } = useAuth()
  const userId = user?.coach?.id
  const [target] = usePortal()
  const styles = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const [bookingLimitValue, setBookingLimitValue] = useState(null)

  const [setBookingLimit] = useMutation(SET_BOOKING_LIMIT, {
    onCompleted: data => {
      const limitChanged = data?.setBookingLimit?.success
      if (!limitChanged) return snackbarError('Booking Limit was not set.')
      setBookingLimitValue(data.setBookingLimit?.coach?.bookingLimit)
      snackbarSuccess(data.setBookingLimit?.message)
    },
    onError: error => {
      window.rg4js &&
        window.rg4js('send', {
          error: error,
          tags: ['set-booking-limit'],
        })
      snackbarError('Booking limit was not set.')
    },
  })

  const crumbs = [
    {
      label: 'Home',
      to: path
        .split('/')
        .slice(0, 2)
        .join('/'),
    },
    {
      label: 'Settings',
      to: path
        .split('/')
        .slice(0, 3)
        .join('/'),
    },
  ]

  const handleLimitChange = e => {
    setBookingLimit({
      variables: { bookingLimit: e.target.value },
    })
  }

  const { data, loading, error } = useQuery(GET_BOOKING_LIMIT_OPTIONS, {
    variables: { id: userId },
  })

  if (loading) return <LinearProgress color="secondary" />
  if (error) return <Error error={error} />

  return (
    <>
      <PageHeader title="Booking limits" crumbs={crumbs} actions={target} />
      <Container className={styles.root}>
        <Typography variant="h6" gutterBottom>
          Booking limits
        </Typography>
        <Grid container item md={4} xs={12}>
          <Box mb={4}>
            <Typography variant="body1" color="textSecondary">
              Determine how far in advance you want to allow your students to
              book with you.
            </Typography>
          </Box>
          <Box mb={2} width="100%">
            <FormControl variant="outlined">
              <TextField
                select
                defaultValue={data?.coach?.bookingLimit}
                value={bookingLimitValue?.value}
                variant="outlined"
                label="Select booking limit"
                onChange={handleLimitChange}
              >
                {data?.bookingLimitOptions.map(blo => (
                  <MenuItem key={blo.value} value={blo.value}>
                    {blo.label}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Box>
        </Grid>
      </Container>
    </>
  )
}

export default BookingLimitsPage
